import { useState } from 'react';
import {
  AppleLogoIcon,
  AndroidLogoIcon,
  CheckCircleIcon,
  LightbulbIcon,
  QuestionIcon,
} from '@phosphor-icons/react';
import { Header } from '@/components/Header';
import { InstallGuideContent } from '@/content/InstallGuideContent';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';

type Platform = 'ios' | 'android';

/**
 * Detects the user's platform for showing the appropriate tab by default
 */
function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'ios';

  const ua = navigator.userAgent.toLowerCase();

  // iOS detection (including iPad with desktop mode)
  if (
    /iphone|ipad|ipod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  ) {
    return 'ios';
  }

  // Android detection
  if (/android/.test(ua)) {
    return 'android';
  }

  // Default to iOS
  return 'ios';
}

export function InstallGuide() {
  const [platform, setPlatform] = useState<Platform>(detectPlatform);

  return (
    <div className="w-full max-w-4xl mx-auto px-4 pb-8">
      <Header enableBack backTo="/" />

      {/* Page Title */}
      <div className="mb-6">
        <h1 className="text-slate-50">{InstallGuideContent.title}</h1>
      </div>

      {/* What is a PWA Section */}
      <section className="rounded-2xl p-5 mb-6 border border-purple-300/10">
        <div className="flex items-start gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 text-purple-300">
              <LightbulbIcon size={24} weight="fill" className="mt-0.5" />
              <h2>{InstallGuideContent.whatIsPwa.title}</h2>
            </div>
            <p className="text-body text-slate-300">
              {InstallGuideContent.whatIsPwa.description}
            </p>
          </div>
        </div>

        {/* Benefits list */}
        <div className="mt-4 space-y-2">
          {InstallGuideContent.whatIsPwa.benefits.map((benefit, index) => (
            <div key={index} className="flex items-center gap-2">
              <CheckCircleIcon
                size={18}
                weight="fill"
                className="text-green-400 flex-shrink-0"
              />
              <span className="text-body text-slate-300">{benefit}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Platform-specific Instructions */}
      <section className="mb-6">
        <h2 className="font-field-54 text-h2 text-slate-50 mb-4">
          How to Install
        </h2>

        <Tabs
          value={platform}
          onValueChange={(v) => setPlatform(v as Platform)}
        >
          <TabsList className="w-full bg-slate-600 mb-4 rounded-xl">
            <TabsTrigger
              value="ios"
              className="flex-1 data-[state=active]:bg-purple-300 data-[state=active]:text-slate-800 rounded-lg"
            >
              <AppleLogoIcon size={18} weight="fill" className="mr-1.5" />
              iOS
            </TabsTrigger>
            <TabsTrigger
              value="android"
              className="flex-1 data-[state=active]:bg-purple-300 data-[state=active]:text-slate-800 rounded-lg"
            >
              <AndroidLogoIcon size={18} weight="fill" className="mr-1.5" />
              Android
            </TabsTrigger>
          </TabsList>

          {/* iOS Instructions */}
          <TabsContent value="ios">
            <InstructionCard
              platform="ios"
              instructions={InstallGuideContent.instructions.ios}
            />
          </TabsContent>

          {/* Android Instructions */}
          <TabsContent value="android">
            <InstructionCard
              platform="android"
              instructions={InstallGuideContent.instructions.android}
            />
          </TabsContent>
        </Tabs>
      </section>

      {/* Help Section */}
      <section className="rounded-2xl p-5 bg-slate-600">
        <div className="flex items-start gap-3">
          <div>
            <div className="flex gap-2 items-center mb-2">
              <QuestionIcon
                size={24}
                weight="bold"
                className="text-slate-400 flex-shrink-0"
              />
              <h2 className="text-large text-slate-200">
                {InstallGuideContent.help.title}
              </h2>
            </div>
            <ul className="space-y-2">
              {InstallGuideContent.help.tips.map((tip, index) => (
                <li
                  key={index}
                  className="text-body text-slate-400 leading-relaxed"
                >
                  • {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Already Installed */}
      <section className="mt-6 text-center">
        <p className="text-small text-slate-500">
          <span className="font-medium text-slate-400">
            {InstallGuideContent.alreadyInstalled.title}
          </span>{' '}
          {InstallGuideContent.alreadyInstalled.description}
        </p>
      </section>
    </div>
  );
}

/**
 * Card component for displaying platform-specific installation instructions
 */
interface InstructionCardProps {
  platform: Platform;
  instructions:
    | typeof InstallGuideContent.instructions.ios
    | typeof InstallGuideContent.instructions.android;
}

function InstructionCard({ instructions }: InstructionCardProps) {
  return (
    <Card className="bg-slate-700 border-slate-600">
      <CardContent className="p-5">
        {/* Header */}
        <div className="mb-5">
          <h3 className="font-field-54 text-lg text-slate-50">
            {instructions.title}
          </h3>
          <p className="text-small text-slate-400">{instructions.subtitle}</p>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {instructions.steps.map((step) => (
            <div key={step.step} className="flex gap-4">
              {/* Step number */}
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-300 text-slate-700 font-field-64 flex items-center justify-center text-body">
                {step.step}
              </div>

              {/* Step content */}
              <div className="flex-1 pt-0.5">
                <h4 className="font-large text-slate-100 mb-1">{step.title}</h4>
                <p className="text-body text-slate-400">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div className="mt-5 pt-4 bg-slate-600 rounded-xl p-4">
          <p className="text-small text-amber-300/80 leading-relaxed">
            <span className="font-medium">Note:</span> {instructions.note}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
